<template>
  <div v-if="node" class="voting-trends">
    <NodeHeader class="mb-8" :node="node"/>
    <SectionSubtitle>Highlights</SectionSubtitle>
    <VotingTrendHighlights :highlights="highlights" :is-loading="isLoadingHighlights" :has-error="highlightsErrored"/>
    <SectionSubtitle>
      Voting Trends
      <template #right>
        <ExportButton :disabled="isLoadingListActivity || isExportingTrends" class="mb-2"
                      @click="votingTrendsExportHandler"/>
      </template>
    </SectionSubtitle>
    <p class="py-2">Voting and sentiment trends</p>
    <VotesChart
        class="votes-charts"
        :totals="chartData"
        :all-totals="zoomChartData"
        @rowSelected="selectedRowHandler"
        :has-error="listActivityErrored"
        :is-loading="!chartData || !zoomChartData || isLoadingListActivity || isLoadingChartsData"
        :time-interval.sync="listActivityParams.timeInterval"
        full-chart
    />
    <SectionSubtitle>
      List Activity
      <template #right>
        <ExportButton :disabled="isLoadingListActivity || isExportingListActivity" class="mb-2"
                      @click="listActivityExportHandler"/>
      </template>
    </SectionSubtitle>
    <ListActivity
        :list-activity="listActivity"
        :list-activity-totals="listActivityTotals"
        :total-lists="totalLists"
        :is-loading="isLoadingListActivity"
        :has-error="listActivityErrored"
        @loadMore="getListActivity(false)"
    >
      <MonthSelector v-model="listActivityParams.startDate" :first-date="firstDayEver"/>
    </ListActivity>
  </div>
</template>

<script>
import NodeHeader from '@/components/Node/NodeHeader';
import SectionSubtitle from '@/components/ui/typography/SectionSubtitle';
import VotingTrendHighlights from '@/components/VotingTrends/VotingTrendHighlights';
import ListActivity from '@/components/VotingTrends/ListActivity';
import MonthSelector from '@/components/ui/MonthSelector';
import ExportButton from '@/components/ui/buttons/ExportButton';
import VotesChart from '@/components/VotingTrends/VotesChart';
import {getNode} from '@/api/nodes';
import {getVotingTrendsCSV, getVotingTrendsListActivityCSV} from '@/lib/votingTrends';
import {getVotingTrendsHighlights, getVotingTrendsListsActivity} from '@/api/votingTrends';
import resizeMessage from '@/mixins/resizeMessage';
import dayjs from 'dayjs';
import debounce from 'lodash/debounce';

export default {
  name: 'VotingTrends',
  props: {
    nodeId: {
      type: String
    }
  },
  components: {
    VotesChart,
    ExportButton,
    MonthSelector,
    ListActivity,
    SectionSubtitle,
    NodeHeader,
    VotingTrendHighlights
  },
  mixins: [
    resizeMessage
  ],
  data() {
    return {
      node: null,
      highlights: null,
      isLoadingHighlights: true,
      highlightsErrored: false,
      listActivity: [],
      listActivityTotals: null,
      isLoadingListActivity: true,
      isLoadingChartsData: true,
      listActivityErrored: false,
      listActivityParams: {
        startDate: dayjs().set('date', 1).format('YYYY-MM-DD'),
        timeInterval: 'monthly',
        includes: 'all'
      },
      chartData: null,
      zoomChartData: null,
      totalLists: 0,
      isExportingTrends: false,
      isExportingListActivity: false
    };
  },
  computed: {
    firstDayEver() {
      if (!this.zoomChartData?.length) {
        return;
      }
      return dayjs(this.zoomChartData[0][0]);
    }
  },
  created() {
    this.getListActivityDebounced = debounce(this.getListActivity, 500, {leading: false, trailing: true});
  },
  watch: {
    nodeId: {
      async handler(val) {
        if (val) {
          (() => this.getHighlights())();
          (() => this.getListActivity())();
          const {node} = await getNode(parseInt(val));
          this.node = node;
        }
      },
      immediate: true
    },
    'listActivityParams.startDate'() {
      this.getListActivityDebounced(true);
    },
    'listActivityParams.timeInterval'() {
      this.listActivityParams.includes = 'chart';
      this.getListActivityDebounced();
    }
  },
  methods: {
    selectedRowHandler([dateObj]) {
      this.listActivityParams.startDate = dayjs(dateObj).set('date', 1).startOf('day').format('YYYY-MM-DD');
    },
    async getHighlights() {
      this.isLoadingHighlights = true;
      this.highlightsErrored = false;
      try {
        const {highlights} = await getVotingTrendsHighlights(this.nodeId);
        this.highlights = highlights;
      }
      catch (e) {
        this.highlightsErrored = true;
        console.error(e);
      }
      this.isLoadingHighlights = false;
    },
    resetParams() {
      this.listActivity = [];
      this.listActivityTotals = null;
      this.listActivityErrored = false;
      this.totalLists = 0;
      this.chartData = null;
      this.zoomChartData = null;
      this.listActivityParams.includes = 'all';
    },
    async getListActivity(refresh = false) {
      try {
        if (refresh) {
          this.resetParams();
        }
        const includesLists = ['lists', 'all'].includes(this.listActivityParams.includes);
        const includesChart = ['chart', 'all'].includes(this.listActivityParams.includes);
        if (includesChart) this.isLoadingChartsData = true;
        if (includesLists) this.isLoadingListActivity = true;
        this.listActivityErrored = false;
        const {lists, totalLists, totals, intervalData, allIntervalData} = await getVotingTrendsListsActivity(this.nodeId, {...this.listActivityParams});
        if (includesLists) {
          this.listActivity = lists;
          this.listActivityTotals = this.getListActivityTotals(totals);
          this.totalLists = Object.freeze(totalLists);
        }
        if (includesChart) {
          this.zoomChartData = Object.freeze(allIntervalData);
          this.chartData = Object.freeze(intervalData);
        }
      }
      catch (e) {
        this.listActivityErrored = true;
        console.error(e);
      }
      this.isLoadingListActivity = false;
      this.isLoadingChartsData = false;
      await this.emitResizeMessage();
    },
    getListActivityTotals({totalUpVotes = 0, totalDownVotes = 0} = {}) {
      const totalVotes = totalUpVotes + totalDownVotes;
      return {
        totalUpVotes,
        totalDownVotes,
        totalVotes,
        sentiment: totalVotes ? (totalUpVotes / totalVotes * 100).toFixed(1) : 0
      };
    },
    async votingTrendsExportHandler() {
      if (this.isLoadingListActivity || this.isExportingTrends) {
        return;
      }
      this.isExportingTrends = true;
      await getVotingTrendsCSV(this.node.id, this.node.name);
      this.isExportingTrends = false;
    },
    async listActivityExportHandler() {
      if (this.isLoadingListActivity || this.isExportingListActivity) {
        return;
      }
      this.isExportingListActivity = true;
      const {startDate} = this.listActivityParams;
      await getVotingTrendsListActivityCSV(this.node.id, this.node.name, {startDate});
      this.isExportingListActivity = false;
    }
  }
};
</script>

<style lang="postcss">
.voting-trends {
  .votes-charts {
    height: 560px;
  }
}
</style>